/* What you follow.
 *
 * A SCREEN, not a dialog. This was a modal, and a modal is the wrong container
 * for a list that grows without limit: it caps its own height, scrolls inside
 * a page that also scrolls, and has to be dismissed before you can do anything
 * with what you found. Somebody who follows forty requests is exactly the
 * person this page is for, and they were the ones it served worst.
 *
 * You FOLLOW on a card and this shows what you followed. The page-level switch
 * at the top is the rarer, broader appetite — "tell me about EVERY new request"
 * is a different thing from "keep me posted on these ones".
 */
import { ArrowLeft, Loader2, Star } from 'lucide-react'
import { Link } from 'react-router-dom'
import {
  useFollowedRequests,
  useFollowing,
  useMe,
  useSetFollowing,
} from '@/api/queries'
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll'
import { PrayerCard } from './PrayerCard'
import { useFocusTarget } from '@/features/notifications/useFocusTarget'

export function FollowingPage() {
  const { data: me } = useMe()
  const list = useFollowedRequests()
  const following = useFollowing()
  const setFollowing = useSetFollowing()
  /* A notification about a followed request can land here rather than on the
     main list, so the same focus handling applies. */
  const focus = useFocusTarget('request')

  const sentinel = useInfiniteScroll(() => list.fetchNextPage(), {
    enabled: list.hasNextPage && !list.isFetchingNextPage,
  })

  const items = list.data?.pages.flatMap((p) => p.items) ?? []
  const onAll = following.data?.following ?? false

  return (
    <div>
      <div className="mb-s4 flex items-center gap-s3">
        <Link
          to="/prayer"
          aria-label="Back to Prayer Requests"
          className="grid h-9 w-9 place-items-center rounded-pill text-ink-2 transition-colors hover:bg-hover"
        >
          <ArrowLeft className="h-5 w-5" aria-hidden />
        </Link>
        <h1 className="text-2xl font-bold tracking-tight">Following</h1>
      </div>

      <label className="mb-s4 flex cursor-pointer items-start gap-s3 rounded-md bg-surface p-s4 shadow-1 hover:bg-hover">
        <input
          type="checkbox"
          checked={onAll}
          onChange={(e) => setFollowing.mutate(e.target.checked)}
          className="mt-0.5 h-5 w-5 shrink-0 accent-[color:var(--cn-brand)]"
        />
        <span>
          <span className="block text-md font-semibold">
            Follow every Prayer Request
          </span>
          <span className="block text-base text-ink-2">
            You will hear about each new one as it is posted.
          </span>
        </span>
      </label>

      {list.isLoading ? (
        <div className="flex justify-center py-s8">
          <Loader2
            className="h-5 w-5 animate-spin text-ink-2"
            aria-label="Loading"
          />
        </div>
      ) : null}

      {!list.isLoading && !items.length ? (
        <div className="rounded-md bg-surface px-s5 py-s8 text-center shadow-1">
          <Star className="mx-auto h-8 w-8 text-ink-2" aria-hidden />
          <p className="mt-s3 text-md font-bold">
            You are not following any requests
          </p>
          <p className="mt-1 text-base text-ink-2">
            Tap the star on a Prayer Request to hear how it turns out.
            Responding to one follows it for you.
          </p>
        </div>
      ) : null}

      {items.length ? (
        <div className="space-y-s3">
          {items.map((request) => (
            <PrayerCard
              key={request.id}
              request={request}
              viewerId={me?.id}
              focusComment={
                focus.entityId === request.id ? focus.commentId : null
              }
            />
          ))}

          {/* Scrolls rather than asking. The modal had a "See more" button
              because a dialog cannot tell when you have reached its bottom. */}
          <div ref={sentinel} className="h-px" aria-hidden />

          {list.isFetchingNextPage ? (
            <div className="flex justify-center py-s4">
              <Loader2
                className="h-5 w-5 animate-spin text-ink-2"
                aria-label="Loading more"
              />
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}
